// js/common/animation.js
import { gsap, ScrollTrigger } from "./gsap";

/**
 * =========================================================
 * FadeUpアニメーション専用モジュール（最適化版）
 * - data-appear="auto|load|scroll"（既定: auto）
 *   auto: 初期表示でビューポート内なら即再生、外ならScrollTriggerで再生
 * =========================================================
 */

/* =========================
   Utilities
========================= */

/** 一要素につき一回だけ初期化させるためのガード */
export function guardOnce(el, key) {
  const attr = "data-anim-" + key;
  if (el.hasAttribute(attr)) return false;
  el.setAttribute(attr, "1");
  return true;
}

/** セレクタのスコープ検索 */
function $$(scope, selector) {
  return (scope || document).querySelectorAll(selector);
}

/** 動きを減らすユーザー設定 */
function prefersReduce() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** ScrollTrigger.refresh を安全に呼ぶ */
function refreshST() {
  if (ScrollTrigger?.refresh) ScrollTrigger.refresh();
  else if (window.ScrollTrigger?.refresh) window.ScrollTrigger.refresh();
}

/** ビューポート内判定（threshold は下端のしきい値比率） */
function isInViewport(el, threshold = 0.9) {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const visibleStart = 0;            // 画面上端
  const visibleEnd = vh * threshold; // 例: 90%位置
  return rect.top < visibleEnd && rect.bottom > visibleStart;
}

/** 要素/グループから表示モードを取得 */
function getAppearMode(el) {
  return (el?.dataset?.appear || "auto").toLowerCase(); // 'auto' | 'load' | 'scroll'
}

/* =========================
   FadeUp アニメーション
========================= */

export function initFadeUp({ scope } = {}) {
  const all = Array.from($$(scope, ".js-fade-up")).filter(el =>
    guardOnce(el, "fadeUp")
  );
  if (!all.length) return;

  if (prefersReduce()) {
    gsap.set(all, { opacity: 1, y: 0 });
    return;
  }

  // 初期状態
  gsap.set(all, { opacity: 0, y: 10 });

  // 即再生対象とスクロール再生対象に振り分け
  const immediate = [];
  const scrollItems = [];

  for (const el of all) {
    const mode = getAppearMode(el);
    if (mode === "load" || (mode === "auto" && isInViewport(el, 0.9))) {
      immediate.push(el);
    } else {
      scrollItems.push(el);
    }
  }

  // 即再生（staggerでまとめて軽く）
  if (immediate.length) {
    gsap.to(immediate, {
      opacity: 1,
      y: 0,
      duration: 1.0,
      ease: "power1.out",
      stagger: 0.08,
    });
  }

  // スクロール再生は batch 化
  if (scrollItems.length) {
    ScrollTrigger.batch(scrollItems, {
      start: "top 80%",
      onEnter: batch => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 1.0,
          ease: "power1.out",
          stagger: 0.08,
        });
      },
      once: true, // 一度きり
    });
  }
}

/* =========================
   初期化関数
========================= */
export function initAnimations({ scope } = {}) {
  initFadeUp({ scope });
}

/* =========================
   Auto init
========================= */

// #付きURLで読み込まれた場合、正しい位置にスクロール
function resnapHash() {
  if (!location.hash) return;
  const id = location.hash.slice(1);
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ block: "start" });
}

window.addEventListener("load", () => {
  // アニメーション初期化
  initAnimations({ scope: document });

  // 画像やフォント読み込み後に1回だけ refresh
  const afterLayoutStable = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        refreshST();   // ← refresh はここだけ
        resnapHash();  // ← アンカー位置補正
      });
    });
  };

  if (document.fonts?.ready) {
    document.fonts.ready.then(afterLayoutStable);
  } else {
    afterLayoutStable();
  }
});

// bfcache復帰時にアンカー位置合わせ
window.addEventListener("pageshow", () => {
  resnapHash();
});

// 同一タブ内でハッシュが変わった時も位置合わせ
window.addEventListener("hashchange", () => {
  resnapHash();
});

/* =========================
   手動 refresh 用フック
========================= */
export function refreshScrollTrigger() {
  refreshST();
}
